import { TaDatePicker } from '@components/shared';
import { Nullable } from '@utils/util.types';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const SeasonalConsultationDates = ({
	startDate,
	endDate,
	onStartChange,
	onEndChange,
}: {
	startDate: Nullable<Dayjs>;
	endDate: Nullable<Dayjs>;
	onStartChange: (value: Nullable<Dayjs>) => void;
	onEndChange: (value: Nullable<Dayjs>) => void;
}) => {
	const { t } = useTranslation();

	// Current date to prevent past date selection
	const today = dayjs();

	// Ensure startDate and endDate are valid Dayjs objects or null
	const validStartDate = startDate
		? dayjs.isDayjs(startDate)
			? startDate
			: dayjs(startDate)
		: null;
	const validEndDate = endDate ? (dayjs.isDayjs(endDate) ? endDate : dayjs(endDate)) : null;
	
	// Log the dates for debugging
	console.log('Start date:', validStartDate?.format('YYYY-MM-DD HH:mm:ss'));
	console.log('End date:', validEndDate?.format('YYYY-MM-DD HH:mm:ss'));

	// Wrap the onChange handlers to ensure they always receive valid Dayjs objects
	const handleStartChange = (value: Nullable<Dayjs>) => {
		// Ensure we're passing a valid Dayjs object or null
		if (value && !dayjs.isDayjs(value)) {
			console.warn('Invalid start date received:', value);
			return;
		}
		console.log('handleStartChange received:', value);
		onStartChange(value);
	};

	const handleEndChange = (value: Nullable<Dayjs>) => {
		// Ensure we're passing a valid Dayjs object or null
		if (value && !dayjs.isDayjs(value)) {
			console.warn('Invalid end date received:', value);
			return;
		}
		console.log('handleEndChange received:', value);
		onEndChange(value);
	};

	const formatDuration = useCallback(
		(start: Nullable<Dayjs>, end: Nullable<Dayjs>) => {
			if (!start || !end) return null;

			const startDayjs = dayjs.isDayjs(start) ? start : dayjs(start);
			const endDayjs = dayjs.isDayjs(end) ? end : dayjs(end);

			const days = endDayjs.diff(startDayjs, 'day');
			const years = Math.floor(days / 365);
			const months = Math.floor((days % 365) / 30);
			const remainingDays = days % 30;

			if (years > 0) {
				return `${years} ${t('year', { count: years })} ${months} ${t('month', { count: months })} ${remainingDays} ${t('day', { count: remainingDays })}`;
			} else if (months > 0) {
				return `${months} ${t('month', { count: months })} ${remainingDays} ${t('day', { count: remainingDays })}`;
			}

			return `${days} ${t('day', { count: days })}`;
		},
		[t]
	);

	return (
		<div className="flex flex-col gap-[4px]">
			<div className="flex flex-col sm:flex-row gap-[20px] items-center justify-center">
				<TaDatePicker
					isRequired
					size="small"
					label={t('findSpecialistPage.startDate')}
					value={validStartDate}
					onChange={handleStartChange}
					minDate={today}
				/>
				<TaDatePicker
					isRequired
					size="small"
					label={t('findSpecialistPage.endDate')}
					value={validEndDate}
					onChange={handleEndChange}
					minDate={validStartDate || today}
				/>
			</div>
			{validStartDate && validEndDate && (
				<div className="text-blue font-roboto text-base font-bold">
					{formatDuration(validStartDate, validEndDate)}
				</div>
			)}
		</div>
	);
};
